"use client";

import { useMemo } from "react";
import { ConfiguratorInput } from "@components/features/configurator/types";

interface SliderInputsProps {
  inputs: ConfiguratorInput[];
  values: Record<string, number>;
  onValueChange: (id: string, value: number) => void;
  className?: string;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function formatValue(value: number, unit?: string): string {
  if (unit) {
    return `${value}${unit}`;
  }
  return String(value);
}

export default function SliderInputs({
  inputs,
  values,
  onValueChange,
  className = "",
}: SliderInputsProps) {
  if (!Array.isArray(inputs) || inputs.length === 0) {
    throw new Error("SliderInputs: `inputs` must be a non-empty array.");
  }

  const valueMap = useMemo(() => values, [values]);

  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 gap-5 ${className}`}>
      {inputs.map((input) => {
        const current = valueMap[input.id];
        if (typeof current !== "number") {
          throw new Error(`SliderInputs: Missing numeric value for input "${input.id}".`);
        }

        return (
          <div key={input.id}>
            <label
              htmlFor={`slider-${input.id}`}
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              {input.label}
            </label>
            <div className="flex items-center gap-3">
              <input
                id={`slider-${input.id}`}
                type="range"
                min={input.min}
                max={input.max}
                step={input.step}
                value={current}
                onChange={(event) => onValueChange(input.id, Number(event.target.value))}
                className="flex-1 accent-abis-primary cursor-pointer touch-none"
                aria-label={input.label}
              />
              <div className="relative">
                <input
                  type="number"
                  min={input.min}
                  max={input.max}
                  step={input.step}
                  value={current}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    if (!Number.isNaN(next)) {
                      onValueChange(input.id, next);
                    }
                  }}
                  onBlur={() =>
                    onValueChange(input.id, clamp(current, input.min, input.max))
                  }
                  className={`w-20 text-center font-bold text-abis-primary text-sm tabular-nums bg-white border border-gray-200 rounded-lg py-1 ${
                    input.unit ? "pr-6" : ""
                  } focus:border-abis-primary focus:ring-1 focus:ring-abis-primary/20 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                  aria-label={`${input.label} Eingabe`}
                />
                {input.unit ? (
                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                    {input.unit}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
              <span>{formatValue(input.min, input.unit)}</span>
              <span className="font-semibold text-abis-primary text-sm">
                {formatValue(current, input.unit)}
              </span>
              <span>{formatValue(input.max, input.unit)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
